import { Link } from "@frontend/lib/link";
import { ProofRow } from "@frontend/components/collab/payment-proof";
import { Callout, Money, Pill } from "@frontend/components/ui";
import type { CollabRoom } from "@creators/backend/lib/collabs/queries";
import { TRIGGER_LABELS } from "@creators/backend/lib/collabs/schedule";
import { formatDate } from "@creators/backend/lib/time";

type Props = {
  room: CollabRoom;
  viewer: "creator" | "brand" | "admin";
  /** Only the owner can add a payout method, so only they get pointed at it. */
  canManagePayouts: boolean;
};

type Milestone = CollabRoom["milestones"][number];
type Proof = CollabRoom["proofs"][number];

const STATUS_PILL = {
  pending: { label: "Scheduled", tone: "neutral" as const },
  due: { label: "Due now", tone: "amber" as const },
  paid: { label: "Awaiting confirmation", tone: "blue" as const },
  confirmed: { label: "Received", tone: "green" as const },
  void: { label: "Cancelled", tone: "neutral" as const },
};

/**
 * The money side of the collab room.
 *
 * Payment happens between the two parties' banks; this panel exists to keep
 * both sides looking at the same version of what has been sent and what has
 * landed. It never claims a payment succeeded on its own — the payer asserts,
 * the creator confirms.
 */
export function PaymentsPanel({ room, viewer, canManagePayouts }: Props) {
  const milestones = room.milestones.filter((m) => m.status !== "void");
  if (milestones.length === 0 || room.currentOffer?.compensationType === "barter") return null;

  const isCreator = viewer === "creator";
  const proofsByMilestone = new Map<string, Proof[]>();
  for (const proof of room.proofs) {
    const list = proofsByMilestone.get(proof.milestoneId) ?? [];
    list.push(proof);
    proofsByMilestone.set(proof.milestoneId, list);
  }

  const due = milestones.filter((m) => m.status === "due");
  const outstanding = due.reduce((sum, m) => sum + m.amount, 0);

  return (
    <section className="card overflow-hidden">
      <div className="flex items-center justify-between gap-2 border-b border-line px-3 py-2">
        <h2 className="text-sm font-semibold text-ink">Payments</h2>
        {outstanding > 0 ? (
          <Pill tone="amber">
            <Money paise={outstanding} size="sm" /> due
          </Pill>
        ) : null}
      </div>

      {viewer === "brand" && outstanding > 0 && !room.payoutDetails ? (
        <div className="border-b border-line px-3 py-2">
          <Callout tone="amber">
            {room.creator.displayName} has not added payment details yet. Ask them to add a method
            before you send anything.
          </Callout>
        </div>
      ) : null}

      {isCreator && !room.payoutDetails ? (
        <div className="border-b border-line px-3 py-2">
          <Callout tone="amber">
            {canManagePayouts ? (
              <>
                <Link
                  href="/settings/payments"
                  className="font-medium text-brand-700 hover:underline"
                >
                  Add payment details in settings
                </Link>{" "}
                — they cannot pay you until you do.
              </>
            ) : (
              <>
                {room.creator.displayName} has no payment method on file. Only the account owner can
                add one.
              </>
            )}
          </Callout>
        </div>
      ) : null}

      {due.length > 1 ? (
        <p className="border-b border-line px-3 py-2 text-[11px] leading-snug text-ink-muted">
          <Money paise={outstanding} size="sm" /> due across {due.length} tranches — pay once;
          they are marked together.
        </p>
      ) : null}

      <ol className="divide-y divide-line">
        {milestones.map((milestone) => (
          <li key={milestone.id} className="px-3 py-2">
            <MilestoneRow
              room={room}
              milestone={milestone}
              proofs={proofsByMilestone.get(milestone.id) ?? []}
            />
          </li>
        ))}
      </ol>

      <p className="border-t border-line px-3 py-1.5 text-[10px] leading-snug text-ink-subtle">
        Money moves directly between you — the platform records it, but never holds it.
      </p>
    </section>
  );
}

function MilestoneRow({
  room,
  milestone,
  proofs,
}: {
  room: CollabRoom;
  milestone: Milestone;
  proofs: Proof[];
}) {
  const pill = STATUS_PILL[milestone.status as keyof typeof STATUS_PILL] ?? STATUS_PILL.pending;
  const livingProof = proofs.find((p) => p.status === "pending") ?? null;
  const rejected = proofs.filter((p) => p.status === "rejected");

  const detail = [
    TRIGGER_LABELS[milestone.trigger],
    milestone.confirmedAt ? `Received ${formatDate(milestone.confirmedAt)}` : null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <div>
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm font-medium leading-tight text-ink">
            <Money paise={milestone.amount} size="sm" />
            <span className="ml-1 font-normal text-ink-muted">({milestone.pct}%)</span>
          </p>
          <p className="mt-0.5 text-[11px] leading-snug text-ink-muted">{detail}</p>
        </div>
        <Pill tone={pill.tone} className="shrink-0">
          {pill.label}
        </Pill>
      </div>

      {livingProof ? (
        <div className="mt-1.5">
          <ProofRow collabId={room.collab.id} proof={livingProof} />
        </div>
      ) : null}

      {rejected.length > 0 && milestone.status === "due" ? (
        <p className="mt-1.5 text-[11px] leading-snug text-alert-600">
          Last attempt was flagged as not received: {rejected[rejected.length - 1].rejectionReason}
        </p>
      ) : null}
    </div>
  );
}
